import { useQueryClient } from '@tanstack/react-query'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { useSignupPromotionsParams } from '@/features/authentication/services/SignupPromotions/hooks'
import { useUser, useUserZip } from '@/features/account/services/User/hooks'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { useCallback } from 'react'
import { useSearchQueryParams } from '@/features/shared/hooks/search'

const useMetroIdForSignupPlans = () => {
  const queryClient = useQueryClient()
  const { zipParam } = useSignupPromotionsParams()
  const data = queryClient.getQueryData(
    shoppingStoresQueryOptions({ address_zip_code: zipParam }).queryKey,
  )
  return data?.delivery_stores[0]?.metro_id ?? 0
}

/** used for during sign up and log in only where sign up params are derived from just URL query params */
const useGetInitialSignupQueryParams = () => {
  const { param = '', referrerUrl = '', zip: zipParam = '', zipcode = '' } = useSearchQueryParams()

  const getInitialSignupParams = useCallback(
    (email = '') => {
      return {
        email: email.toLowerCase(),
        zip_code: String(zipParam || zipcode),
        param: String(param),
        referring_url: String(referrerUrl),
      }
    },
    [param, referrerUrl, zipParam, zipcode],
  )

  return { getInitialSignupParams }
}

/** used for post sign up and log in only where sign up params are derived from both URL query params and user data */
export const useSignupPlansQueryKeyParams = () => {
  const { getInitialSignupParams } = useGetInitialSignupQueryParams()
  const storeParams = useStoreParams()
  const { metro_id } = storeParams
  const zip = useUserZip()
  const userEmail = useUser()?.email ?? ''
  const signupPlansMetroId = useMetroIdForSignupPlans()
  const metroId = signupPlansMetroId || metro_id
  const getQueryKeyParams = useCallback(
    (email = '') => {
      const initialSignupParams = getInitialSignupParams(email)
      /** We would like to identify the source of zip and metroid to see if we are relying on useStoreParam fields that we use as a backup **/
      return {
        ...initialSignupParams,
        email: userEmail || initialSignupParams.email,
        metro_id: metroId,
        zip_code: initialSignupParams.zip_code || zip,
      }
    },
    [getInitialSignupParams, metroId, userEmail, zip],
  )

  return { getQueryKeyParams }
}
